// ============================================================================
// TRAINING ASSESSMENT API CLIENT
// Competence checklist for the Tier 2 facilitator course.
// See .claude/plans/training-assessment-hook.md
// ============================================================================

import { api } from './api';

export type CriterionStatus = 'met' | 'not_met';
export type TrainingReviewStatus = 'not_submitted' | 'submitted' | 'signed_off' | 'returned';

export interface CriterionEvidence {
  type: 'session' | 'avatar' | 'contribution' | 'project' | 'transcript';
  id: string;
  label: string;
  href: string;
}

export interface CriterionResult {
  id: string;
  label: string;
  status: CriterionStatus;
  detail: string;
  evidence: CriterionEvidence[];
  quality?: { score: number; rationale: string };
}

export interface TrainingReview {
  status: TrainingReviewStatus;
  submittedAt?: string;
  reviewerId?: string;
  reviewerName?: string;
  reviewedAt?: string;
  notes?: string;
}

export interface AssessmentResult {
  projectId: string;
  userId: string;
  evaluatedAt: string;
  criteria: CriterionResult[];
  metCount: number;
  totalCount: number;
  allMet: boolean;
  review: TrainingReview;
  fromSnapshot?: boolean;
}

export interface TrainingProgress {
  projectId: string;
  userId: string;
  userDisplayName?: string;
  userEmail?: string;
  walkthroughStepIndex: number;
  walkthroughStartedAt?: string;
  walkthroughCompletedAt?: string;
  events: {
    secondScreenConfigured?: string;
    skillDownloaded?: Record<string, string>;
  };
  review: TrainingReview;
}

export async function getAssessment(projectId: string, userId?: string): Promise<AssessmentResult> {
  const res = await api.get(`/api/projects/${projectId}/training/assessment`, {
    params: userId ? { userId } : undefined,
  });
  return res.data.assessment;
}

export async function listTrainees(projectId: string): Promise<TrainingProgress[]> {
  const res = await api.get(`/api/projects/${projectId}/training/trainees`);
  return res.data.trainees ?? [];
}

export async function getWalkthroughProgress(projectId: string): Promise<TrainingProgress> {
  const res = await api.get(`/api/projects/${projectId}/training/progress`);
  return res.data.progress;
}

export async function saveWalkthroughProgress(
  projectId: string,
  stepIndex: number,
  completed?: boolean
): Promise<TrainingProgress> {
  const res = await api.patch(`/api/projects/${projectId}/training/progress`, { stepIndex, completed });
  return res.data.progress;
}

/**
 * Records an action that leaves no other trace. Callers should not await this
 * in a UI path — a failed write must never block the action itself.
 */
export async function recordTrainingEvent(
  projectId: string,
  event: { type: 'secondScreenConfigured' } | { type: 'skillDownloaded'; skillId: string }
): Promise<void> {
  await api.post(`/api/projects/${projectId}/training/events`, event);
}

export async function submitForReview(projectId: string): Promise<AssessmentResult> {
  const res = await api.post(`/api/projects/${projectId}/training/submit`, {});
  return res.data.assessment;
}

export async function signOffTraining(
  projectId: string,
  userId: string,
  outcome: 'passed' | 'returned',
  notes?: string,
  override?: boolean
): Promise<AssessmentResult> {
  const res = await api.post(`/api/projects/${projectId}/training/sign-off`, {
    userId, outcome, notes, override,
  });
  return res.data.assessment;
}
